/* eslint-disable react/display-name */
import React, { useEffect, useMemo, useState } from 'react';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import { translate } from 'components/Translation';
import { makeStyles } from '@material-ui/core';
import styles from 'assets/jss/material-dashboard-pro-react/views/prepaidLoyalty/setOfNumbersDialogStyle';
import { BodySBold } from 'components/Typography/Typography';
import CustomTable from 'components/CustomTable/CustomTable';
import { PhoneNumberCell } from 'components/Cells/Cells';
import TableHeader from 'components/TableHeader/TableHeader';
import { SubscriptionDetails } from 'utils/api/models/prepaidLoyalty/subscriptionDetails';
import { LoyaltyGroupService } from 'services/loyaltyGroups/loyaltyGroups';
import { finalize } from 'rxjs/operators';
import SubscriptionTooltip from './SubscriptionTooltip';
import { SingleSubscription, subscriptionStatus } from 'utils/api/models/topUpAddons/singleSubscription';
import Button from 'components/CustomButtons/Button';
import { dispatch } from 'redux/store';
import { setToast } from 'redux/modules/toast/actions';
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog';
import moment from 'moment';

const CREDIT_CARD = 'CREDIT_CARD';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IGroupDetailsDialog {
	isOpen: boolean;
	hideDialog: () => void;
	subscription?: SingleSubscription;
	groupSubscriptions: () => void;
}

const SetOfNumbersDialog = ({ isOpen, hideDialog, subscription, groupSubscriptions }: IGroupDetailsDialog) => {
	const [loading, setLoading] = useState<boolean>(false);
	const [currentSubscriptionDetails, setCurrentSubscriptionDetails] = useState<SubscriptionDetails | undefined>();
	const [searchValue, setSearchValue] = useState<string>('');
	const [numberToUnsubscribe, setNumberToUnsubscribe] = useState<string>('');
	const [creditCardWarning, setCreditCardWarning] = useState<boolean>(false);
	const classes = useStyles();

	const startUnsubscribe = () => {
		if (creditCardWarning === true) {
			hideCreditCardWarning();
		}
		const numberData = currentSubscriptionDetails?.numbers.find((nmbr) => nmbr.number === numberToUnsubscribe);
		if (numberData && subscription) {
			setLoading(true);
			LoyaltyGroupService.unsubscribeNumber({
				loyaltySubscriptionId: subscription.id,
				loyaltyNumberId: +numberData.id,
			})
				.pipe(
					finalize(() => {
						setLoading(false);
					})
				)
				.subscribe(
					(data) => {
						if (data.response === true) {
							hideDialog();
							dispatch(
								setToast({
									error: false,
									label: translate(
										'prepaid.loyalty.manage.service.details.table.phone.unsubscribed',
										numberData.number
									),
								})
							);
							groupSubscriptions();
						}
					},
					() => {
						hideDialog();
						dispatch(
							setToast({
								error: true,
								label: translate('error.generic-title'),
							})
						);
					}
				);
		}
	};

	const checkBeforeUnsubscribe = (original: string) => {
		setNumberToUnsubscribe(original);
		if (currentSubscriptionDetails?.paymentType.toString() === CREDIT_CARD) {
			setCreditCardWarning(true);
		} else {
			startUnsubscribe();
		}
	};

	const hideCreditCardWarning = () => {
		setCreditCardWarning(false);
	};

	const getSubscriptions = () => {
		if (subscription?.id) {
			setLoading(true);
			LoyaltyGroupService.getSubscriptionDetails(subscription?.id)
				.pipe(
					finalize(() => {
						setLoading(false);
					})
				)
				.subscribe((subscriptionDetails) => {
					setCurrentSubscriptionDetails(subscriptionDetails);
				});
		}
	};

	useEffect(() => {
		getSubscriptions();
	}, [subscription]);

	const currentSubscriptionNumbers: string[] = useMemo(() => {
		return (currentSubscriptionDetails?.numbers || []).map((num) => num.number);
	}, [currentSubscriptionDetails]);

	const filteredNumbers: string[] = useMemo(() => {
		return (currentSubscriptionNumbers || []).filter((num) => num.includes(searchValue.toLowerCase()));
	}, [currentSubscriptionNumbers, searchValue]);

	const columns: any[] =
		subscription?.status === subscriptionStatus.ACTIVE
			? [
					{
						sortable: true,
						accessor: '',
						Header: translate('prepaid.loyalty.manage.service.details.table.phone.number'),
						Cell: ({ original }: { original: string }) => (
							<PhoneNumberCell color="primary-light" text={original} />
						),
					},
					{
						sortable: false,
						Header: translate('unsubscribe'),
						Cell: ({ original }: { original: string }) => (
							<Button color="tertiary-danger" onClick={() => checkBeforeUnsubscribe(original)}>
								<>
									<span>&#x2716;</span>
									<span>&nbsp;</span>
									{translate('unsubscribe')}
								</>
							</Button>
						),
					},
			  ]
			: [
					{
						sortable: true,
						accessor: '',
						Header: translate('prepaid.loyalty.manage.service.details.table.phone.number'),
						Cell: ({ original }: { original: string }) => (
							<PhoneNumberCell color="primary-light" text={original} />
						),
					},
			  ];

	return (
		<>
			<CustomDialog
				header={translate('prepaid.loyalty.manage.service.details.table.subscriptions.numbers')}
				description={
					<>
						<div className={classes.description}>
							<BodySBold color="primary-light" inline disableMargin>
								{subscription?.status === subscriptionStatus.ACTIVE
									? translate('prepaid.loyalty.active.subscription.title')
									: translate('prepaid.loyalty.finished.subscription.title')}
							</BodySBold>
							<SubscriptionTooltip
								target={currentSubscriptionDetails?.target}
								endDate={currentSubscriptionDetails?.endDate}
								type={currentSubscriptionDetails?.type}
							/>
						</div>
					</>
				}
				loading={loading}
				isOpen={isOpen}
				hideDialog={hideDialog}
				dialogBody={
					<>
						<div className={classes.tableWrapper}>
							<TableHeader
								searchInputValue={searchValue}
								setSearchInputValue={(value) => setSearchValue(value)}
								stroke={true}
							/>
							<CustomTable
								withHeader
								total={(filteredNumbers || [])?.length}
								tableProps={{
									data: filteredNumbers || [],
									columns: columns,
									defaultPageSize: 5,
									stroke: true,
								}}
							/>
						</div>
					</>
				}
			/>
			<ConfirmationDialog
				isOpen={creditCardWarning}
				hideDialog={hideCreditCardWarning}
				header={''}
				loading={loading}
				dialogBody={
					<>
						<BodySBold color="brand-secondary">
							{translate(
								'prepaid.loyalty.payment.type.card.upfront',
								moment(currentSubscriptionDetails?.endDate).format('DD/MM/YYYY')
							)}
						</BodySBold>
					</>
				}
				buttons={[
					{
						text: translate('cancel'),
						onClick: hideCreditCardWarning,
						color: 'tertiary-light',
					},
					{
						text: translate('unsubscribe'),
						onClick: startUnsubscribe,
						color: 'primary-light',
					},
				]}
			/>
			;
		</>
	);
};

export default SetOfNumbersDialog;
